import React, { useRef, useEffect, useState } from 'react';

/**
 * VideoBackground — self-hosted looping video behind a section.
 *
 * Usage:
 *   <VideoBackground
 *     src="/videos/hero-bg.mp4"
 *     fallbackImage="/images/hero/hero-fallback.jpg"
 *     overlayColor="rgba(15, 17, 24, 0.65)"
 *     overlayGradient="linear-gradient(135deg, rgba(26,31,58,0.8) 0%, rgba(15,17,24,0.7) 100%)"
 *   />
 *
 * Props:
 *   src              — path to the MP4 in /public/videos/
 *   fallbackImage    — path to a static JPG used when video can't load or
 *                      prefers-reduced-motion is set (place in /public/images/)
 *   overlayColor     — solid or rgba color over the video (for text legibility)
 *   overlayGradient  — optional gradient overlay (overrides overlayColor if set)
 *   posterImage      — first-frame poster JPG shown before video loads
 */

const VideoBackground = ({
  src,
  fallbackImage,
  posterImage,
  overlayColor      = 'rgba(15, 17, 24, 0.60)',
  overlayGradient   = null,
  className         = '',
}) => {
  const videoRef   = useRef(null);
  const [loaded,   setLoaded]   = useState(false);
  const [useVideo, setUseVideo] = useState(true);

  // Respect prefers-reduced-motion — show static fallback instead
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) setUseVideo(false);
    const handler = (e) => setUseVideo(!e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Attempt to play — on error fall back to static image
  useEffect(() => {
    if (!useVideo || !videoRef.current) return;
    const vid = videoRef.current;
    const onError = () => setUseVideo(false);
    vid.addEventListener('error', onError);
    return () => vid.removeEventListener('error', onError);
  }, [useVideo]);

  const overlayStyle = overlayGradient
    ? { background: overlayGradient }
    : { background: overlayColor };

  return (
    <>
      <style>{`
        .vbg-root {
          position: absolute;
          inset: 0;
          overflow: hidden;
          z-index: 0;
        }

        .vbg-media {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          /* No pointer events — it's purely decorative */
          pointer-events: none;
          /* Start invisible; fade in once ready */
          opacity: 0;
          transition: opacity 0.8s ease;
        }

        .vbg-media.vbg-ready {
          opacity: 1;
        }

        .vbg-overlay {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 1;
        }
      `}</style>

      <div className={`vbg-root ${className}`} aria-hidden="true">

        {/* ── Video (when available and motion OK) ── */}
        {useVideo && src ? (
          <video
            ref={videoRef}
            className={`vbg-media${loaded ? ' vbg-ready' : ''}`}
            src={src}
            poster={posterImage || fallbackImage}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onCanPlay={() => setLoaded(true)}
          />
        ) : (
          /* ── Static fallback image ── */
          fallbackImage && (
            <img
              className="vbg-media vbg-ready"
              src={fallbackImage}
              alt=""
              loading="eager"
            />
          )
        )}

        {/* ── Dark overlay so text stays readable ── */}
        <div className="vbg-overlay" style={overlayStyle} />
      </div>
    </>
  );
};

export default VideoBackground;
